import type { Messages } from '@tms/i18n';

/**
 * Семантика цвета статуса (разд. 2.3 спецификации кабинета).
 *
 * Цвет кодирует **требует ли заказ внимания**, а не стадию. Диспетчер ищет проблемы,
 * а не любуется прогрессом: если «в пути» зелёное, а «доставлен» синее, взгляд не за
 * что зацепиться.
 *
 * Та же таблица реализована в приложении курьера (`apps/driver/src/theme/tokens.ts`):
 * расхождение семантики между кабинетом и приложением читается как разные системы.
 */

export type Tone = 'ok' | 'progress' | 'wait' | 'warn' | 'danger' | 'neutral';

export function toneForOrder(status: string, goodsLocation?: string | null): Tone {
  // Товар у курьера важнее статуса: заказ может быть отменён, а коробка едет обратно.
  if (goodsLocation === 'courier') {
    return 'wait';
  }

  if (status === 'unassigned' || status === 'not_delivered') {
    return 'danger';
  }

  if (status === 'returned') {
    return 'warn';
  }

  if (status === 'delivered' || status === 'resolved_cancelled') {
    return 'ok';
  }

  if (status === 'assigned' || status === 'picked_up' || status === 'in_transit') {
    return 'progress';
  }

  return 'neutral';
}

/**
 * Подпись статуса — из единой таблицы (разд. 2.3.1).
 *
 * Придумывать подписи на месте нельзя: расхождение подписей между экранами читается
 * как разные сущности. Отсюда и общий пакет локалей с приложением курьера.
 */
export function statusLabel(messages: Messages, status: string): string {
  const table = messages.status as Record<string, string | undefined>;

  if (status.startsWith('resolved')) {
    return table['resolved'] ?? status;
  }

  return table[status] ?? status;
}

/** Цветовые переменные тона: заливка и текст. */
/**
 * Подпись кода из словаря; кода нет в словаре — сам код. Новый код с сервера не
 * должен пропадать с экрана только потому, что для него ещё нет слов.
 */
export function codeLabel(labels: Readonly<Record<string, string>>, code: string): string {
  return labels[code] ?? code;
}

export function toneVars(tone: Tone): { color: string; background: string } {
  return { color: `var(--${tone})`, background: `var(--${tone}-weak)` };
}
